import {type BigId, LobbyImage} from '@colonist/types'
import {
    type FriendRequestReceivedData,
    type FriendRoomInviteNotificationData,
    type FriendStatusData,
    type RoomInviteResponse,
    OnlineStatus,
} from '../../../../shared/friends/FriendTypes.ts'
import {SocketLobbySend} from '../socket/SocketLobbySend.ts'
import {UIUtilsNotifications} from '../notifications/UIUtilsNotifications.ts'
import {type UINotificationPanelController} from '../notifications/UINotificationPanelController.ts'
import {TimeUtils} from '@colonist/utils'
import {type CTANotificationTypeData, type UICTANotificationView} from '../notifications/UICTANotificationView.ts'
import {type TranslationObject} from '@colonist/i18n'
import {NotificationTypeEnum} from '../../../../shared/NotificationTypes.ts'
import {socket, uiManagerEvents} from '../EntryShared.ts'
import {AudioManager, AudioType} from '@colonist/audio'

export abstract class UIOnlineFriendController {
    private readonly notificationPanelController: UINotificationPanelController | undefined
    private readonly roomInviteNotifications: Map<string, UICTANotificationView[]> = new Map()
    private readonly friendRequestNotifications: Map<string, UICTANotificationView[]> = new Map()
    private readonly onlineFriendUsers: Map<BigId, FriendStatusData> = new Map()
    private readonly lastNotificationTimeForUser: Map<BigId, number> = new Map()
    private readonly ONLINE_NOTIFICATION_THROTTLE_MS: number = TimeUtils.SECONDS_IN_MINUTE * TimeUtils.MILLISECONDS
    private static lastOnlineStatus: OnlineStatus = OnlineStatus.OnlineAndIdle

    constructor(notificationPanelController: UINotificationPanelController | undefined) {
        this.notificationPanelController = notificationPanelController
    }

    init() {
        socket.controller.registerSessionEstablishedObserver(() => {
            this.removeAllInvites()
            UIOnlineFriendController.registerToFriendService()
        })
    }

    private static registerToFriendService() {
        SocketLobbySend.registerToFriendService()
        SocketLobbySend.changedOnlineStatus(this.lastOnlineStatus)
        UIUtilsNotifications.delayNotificationDataRequest(() => SocketLobbySend.getAllFriendRoomInvitesReceived())
    }

    private static setOnlineStatus(onlineStatus: OnlineStatus) {
        this.lastOnlineStatus = onlineStatus
        SocketLobbySend.changedOnlineStatus(onlineStatus)
    }

    static userOnlineAndIdle() {
        this.setOnlineStatus(OnlineStatus.OnlineAndIdle)
    }

    static userEnteredRoom() {
        this.setOnlineStatus(OnlineStatus.InRoom)
        UIUtilsNotifications.delayNotificationDataRequest(() => SocketLobbySend.getAllFriendsOnlineStatus())
    }

    static userEnteredGame() {
        this.setOnlineStatus(OnlineStatus.InGame)
    }

    static inviteFriendToRoom(receiverUserId: BigId, roomId: string) {
        SocketLobbySend.inviteFriendToRoom(receiverUserId, roomId)
    }

    static respondToFriendRoomInvite(roomId: string, accept: boolean, senderUserId: BigId) {
        if(accept) uiManagerEvents.leaveRoomAndJoinRoom(roomId)
        SocketLobbySend.respondToFriendRoomInvite(roomId, accept, senderUserId)
    }

    updateFriendOnlineStatus(friendData: FriendStatusData) {
        this.displayNotificationIfUserCameOnline(friendData)
    }

    updateAllFriendsOnlineStatus(friends: FriendStatusData[]) {
        this.onlineFriendUsers.clear()
        for(const friend of friends) this.onlineFriendUsers.set(friend.userId, friend)
    }

    friendRespondedToRoomInvite(responseData: RoomInviteResponse) {
        if(responseData.response) return
        const declinedTranslationObj: TranslationObject = {key: 'strings:roomPage.declinedInvite', options: {username: responseData.responderUsername}}
        this.showNotification(LobbyImage.Lightning, declinedTranslationObj)
    }

    abstract roomInviteSentToFriendSuccessfully(receiverUserId: BigId)

    receivedRoomInvites(roomInvites: FriendRoomInviteNotificationData[]) {
        if(roomInvites.length > 0) AudioManager.play(AudioType.RoomSettingUpdated)
        for(const roomInvite of roomInvites) {
            this.showRoomInvitePopupNotification(roomInvite)
            this.addRoomInviteNotificationToPanel(roomInvite)
        }
    }

    receivedFriendRequest(friendRequest: FriendRequestReceivedData) {
        AudioManager.play(AudioType.RoomSettingUpdated)
        this.showFriendRequestPopupNotification(friendRequest)
        this.addFriendRequestNotificationToPanel(friendRequest)
    }

    private addRoomInviteNotificationToPanel(roomInvite: FriendRoomInviteNotificationData) {
        const notificationTypeData: CTANotificationTypeData = {
            type: NotificationTypeEnum.LiveRoomInvite,
            roomId: roomInvite.roomId,
            senderUsername: roomInvite.senderUsername,
            senderUserId: roomInvite.senderUserId,
            senderIcon: roomInvite.senderIcon,
        }
        this.addCTANotificationToPanel(notificationTypeData, roomInvite.dateCreated, roomInvite.roomId, this.roomInviteNotifications)
    }

    private addFriendRequestNotificationToPanel(friendRequest: FriendRequestReceivedData) {
        const notificationTypeData: CTANotificationTypeData = {
            type: NotificationTypeEnum.FriendRequestReceived,
            username: friendRequest.username,
        }
        this.addCTANotificationToPanel(notificationTypeData, friendRequest.dateCreated, friendRequest.username, this.friendRequestNotifications)
    }

    private addCTANotificationToPanel(notificationTypeData: CTANotificationTypeData, dateCreated: Date, key: string, notificationsMap: Map<string, UICTANotificationView[]>) {
        if(this.notificationPanelController == undefined) return
        const newNotification = this.notificationPanelController.addCTANotification({typeData: notificationTypeData, dateReceived: dateCreated})

        const notificationsFromMap = notificationsMap.get(key)
        notificationsFromMap ? notificationsFromMap.push(newNotification) : notificationsMap.set(key, [newNotification])
    }

    private showRoomInvitePopupNotification(roomInvite: FriendRoomInviteNotificationData) {
        const notificationTranslationObj: TranslationObject = {key: 'strings:roomPage.userInvitedToRoom', options: {context: 'checkNotifications', username: roomInvite.senderUsername, roomId: roomInvite.roomId}}
        this.showJoinRoomNotification(roomInvite.senderIcon, notificationTranslationObj, roomInvite)
    }

    private showFriendRequestPopupNotification(friendRequest: FriendRequestReceivedData) {
        const notificationTranslationObj: TranslationObject = {key: 'strings:popups.notification.userSentYouFriendRequest', options: {context: 'noLink', username: friendRequest.username}}
        this.showNotification(LobbyImage.Friends, notificationTranslationObj)
    }

    removeRoomInvitesWithRoomId(roomId: string) {
        const notifications = this.roomInviteNotifications.get(roomId)
        if(!notifications) return
        for(const notification of notifications) this.notificationPanelController?.removeCTANotification(notification)
        this.roomInviteNotifications.delete(roomId)
    }

    removeAllInvites() {
        for(const roomId of this.roomInviteNotifications.keys()) {
            this.removeRoomInvitesWithRoomId(roomId)
        }
    }

    private displayNotificationIfUserCameOnline(friendData: FriendStatusData) {
        const enoughTimePassed = this.enoughTimePassedSinceLastNotificationForUser(friendData.userId)
        const didUserComeOnline = this.didUserComeOnline(friendData)
        const canDisplayOnlineNotification = enoughTimePassed && didUserComeOnline
        if(canDisplayOnlineNotification) this.showNotification(friendData.icon, {key: 'strings:roomPage.friendOnline', options: {username: friendData.username}})
        this.lastNotificationTimeForUser.set(friendData.userId, Date.now())

        const isNewStatusOffline = friendData.onlineStatus == OnlineStatus.Offline
        if(isNewStatusOffline) this.onlineFriendUsers.delete(friendData.userId)
        else this.onlineFriendUsers.set(friendData.userId, friendData)
    }

    private enoughTimePassedSinceLastNotificationForUser(userId: BigId): boolean {
        const lastNotificationTime = this.lastNotificationTimeForUser.get(userId)
        if(lastNotificationTime == undefined) return true
        const enoughTimePassed = Date.now() - lastNotificationTime > this.ONLINE_NOTIFICATION_THROTTLE_MS
        return enoughTimePassed
    }

    private didUserComeOnline(newFriendData: FriendStatusData) {
        const oldFriendData = this.onlineFriendUsers.get(newFriendData.userId)
        const isNewStatusOnline = newFriendData.onlineStatus == OnlineStatus.OnlineAndIdle
        const isOldStatusOffline = oldFriendData?.onlineStatus == OnlineStatus.Offline || oldFriendData == undefined
        return isNewStatusOnline && isOldStatusOffline
    }

    abstract showNotification(icon: LobbyImage, translationObj: TranslationObject, roomId?: string)
    abstract showJoinRoomNotification(icon: LobbyImage, translationObj: TranslationObject, roomInvite: FriendRoomInviteNotificationData)
}
